<template>
  <v-overlay v-if="isFetchingCurrentOffer" z-index="10">
    <v-progress-circular indeterminate size="64" />
  </v-overlay>
  <AppLayout
    v-else-if="currentOffer"
    parent="/offers"
    class="offers"
    :title="candidateName"
    :isLoading="isFetchingCurrentOffer"
    centered
    isDetails
  >
    <template v-slot:header>
      <Chip
        :color="getColorByStatus(currentOffer.status)"
        class="chip--status mt-1"
        shrink
        :class="{ 'ml-3': !isMobile }"
      >
        {{ currentOffer.status | upperCase }}
      </Chip>
    </template>
    <template v-slot:body>
      <div>
        <div class="text-center">
          <PrimaryButton
            class="mx-auto booking-link narrow-button"
            @click.native="viewBooking"
            :disabled="!bookingId"
            text
            shrink
          >
            <v-icon class="mr-1">mdi-briefcase-outline</v-icon>
            {{ bookingLink }}
          </PrimaryButton>
        </div>
        <div class="container my-2 pt-1">
          <Permissioned section="offers.view">
            <Comment
              permission="comments"
              :isAdding="isAddingComments"
              :isLoading="isFetchingOfferComments"
              v-if="showComments || commentsSize"
              ref="comments"
              :comments="offerComments"
              @comment="createNewComment"
              :minimizable="false"
              focusOnInit
            />
          </Permissioned>
          <Role :bookingId="bookingId" />
          <h2 class="my-3">Candidate</h2>
          <LabeledText label="Name">
            {{ candidateName }}
          </LabeledText>
          <TextWithVisibilityToggle
            label="Criminal Convictions"
            :text="currentOffer.unspentCriminalConvictions || 'No'"
          />
          <Permissioned section="workers">
            <LabeledText
              label="Date of Birth"
              permission="view.dob"
              v-if="candidate.dob"
            >
              {{ candidate.dob | baseDate }}
            </LabeledText>
          </Permissioned>
          <h2 class="my-3">Offer</h2>
          <OfferDetails :currentOffer="currentOffer" />
          <div class="mt-2" v-if="isFetchingReferences">
            <v-skeleton-loader v-for="i in 5" :key="i" type="list-item" />
          </div>
          <div class="mt-2" v-else-if="showReferences">
            <Reference
              v-for="reference in formattedReferences"
              :key="reference.id"
              :reference="reference"
              :candidate="candidate"
              @updated="updateReferences"
            />
          </div>
          <Permissioned class="mb-3" section="offers">
            <AddReferenceAction
              class="full-width"
              permission="add-reference"
              :candidate="candidate"
            />
          </Permissioned>
          <Permissioned section="offers">
            <div
              permission="edit-groups"
              class="d-flex justify-space-between align-center full-width mb-5"
            >
              <h2>Forms & Documents</h2>
              <EditOfferGroupsAction />
            </div>
          </Permissioned>
          <OfferGroups
            v-for="group in sortedOfferGroups"
            :key="group.id"
            :group="group"
          />
          <Permissioned section="offers">
            <Contracts permission="contract-view" :contracts="contracts" />
          </Permissioned>
        </div>
        <BaseFooter>
          <ExpandedButtons>
            <Permissioned
              :vertical="isMobile"
              section="offers"
              class="actions-bar"
            >
              <SubmitForApproval permission="submit-for-approval" />
              <WithdrawOffer permission="withdraw-offer" />
              <SendOfferMessage
                permission="send-offer-message"
                :id="currentOfferId"
              />
              <CompleteOffer permission="mark-as-complete" />
              <RejectOffer permission="reject-offer" />
              <ApproveOffer permission="approve-offer" />
              <AcceptOffer permission="accept-offer" />
              <ShowCommentsButton
                permission="add-comment"
                v-if="!commentsSize"
                @click.native="openCommentsSection"
              />
              <RequestReferences permission="request-reference" />
            </Permissioned>
          </ExpandedButtons>
        </BaseFooter>
      </div>
    </template>
  </AppLayout>
</template>
<script>
import AppLayout from "@/components/common/base-layouts/AppLayout";
import Comment from "@/components/common/Comment";
import SendOfferMessage from "@/views/offers/actions/SendOfferMessageAction";
import WithdrawOffer from "@/views/offers/actions/WithdrawOfferAction";
import RejectOffer from "@/views/offers/actions/RejectOfferAction";
import SubmitForApproval from "@/views/offers/actions/SubmitForApprovalAction";
import ApproveOffer from "@/views/offers/actions/ApproveOfferAction";
import AcceptOffer from "@/views/offers/actions/AcceptOfferAction";
import CompleteOffer from "@/views/offers/actions/CompleteOfferAction";
import BaseFooter from "@/components/common/BaseFooter";
import Permissioned from "@/components/common/Permissioned";
import {
  FETCH_CURRENT_OFFER,
  FETCH_OFFER_COMMENTS,
  ADD_OFFER_COMMENT
} from "@/store/modules/offers/actions";
import { OFFERS_NAMESPACE } from "@/store/modules/offers";
import { createNamespacedHelpers } from "vuex";
import { getCandidateName } from "@/utils/users";
import { size } from "lodash";
import ShowCommentsButton from "@/components/common/Button/ShowCommentsButton";
import { scrollToPageTop } from "@/utils/helpers";
import { getColorByStatus, getSortedOfferGroups } from "@/utils/offers";
import Chip from "@/components/common/Chip";
import { isPermissioned } from "@/utils/permissions";
import Reference from "@/views/offers/components/compliances/references/ReferenceItem";
import { FETCH_REFERENCES } from "@/store/modules/references/actions";
import { REFERENCES_NAMESPACE } from "@/store/modules/references";
import { copyState } from "@/utils/helpers";
import RequestReferences from "@/views/offers/actions/RequestReferencesAction";
import AddReferenceAction from "@/views/offers/components/compliances/actions/AddReferenceAction";
import { getReferences } from "@/utils/offers";
import Role from "@/views/offers/components/Role";
import TextWithVisibilityToggle from "@/components/common/BaseDetails/TextWithVisibilityToggle";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import LabeledText from "@/components/common/BaseDetails/LabeledText";
import OfferDetails from "@/views/offers/components/OfferDetails";
import ExpandedButtons from "@/components/common/ExpandedButtons";
import OfferGroups from "@/views/offers/components/OfferGroups";
import Contracts from "@/views/offers/components/contracts/Contracts";
import EditOfferGroupsAction from "@/views/offers/actions/EditOfferGroupsAction";

const {
  mapActions: mapOfferActions,
  mapState: mapOfferState
} = createNamespacedHelpers(OFFERS_NAMESPACE);
const {
  mapActions: referenceActions,
  mapState: referenceState
} = createNamespacedHelpers(REFERENCES_NAMESPACE);
const OFFER_STAGE_INDEX = 0;

export default {
  components: {
    AppLayout,
    Comment,
    SendOfferMessage,
    BaseFooter,
    WithdrawOffer,
    CompleteOffer,
    Permissioned,
    ApproveOffer,
    SubmitForApproval,
    ShowCommentsButton,
    RejectOffer,
    AcceptOffer,
    Chip,
    Reference,
    AddReferenceAction,
    RequestReferences,
    Role,
    TextWithVisibilityToggle,
    PrimaryButton,
    LabeledText,
    OfferDetails,
    OfferGroups,
    ExpandedButtons,
    EditOfferGroupsAction,
    Contracts
  },
  data() {
    return {
      showComments: false,
      isAddingComments: false,
      offerStageIndex: OFFER_STAGE_INDEX,
      sortedOfferGroups: []
    };
  },
  async created() {
    await this.fetchCurrentOffer(this.currentOfferId);
    if (!this.currentOffer) {
      return this.$router.push("/pageNotFound");
    }
    this.adjustOfferGroups(this.currentOffer.offerGroups);
    document.title = this.pageTitle;
    if (this.shouldFetchComments) {
      this.fetchOfferComments(this.currentOfferId);
    }
    await this.fetchReferences(this.referencesRequestParams);
  },
  computed: {
    ...mapOfferState([
      "currentOffer",
      "isFetchingCurrentOffer",
      "offerComments",
      "isFetchingOfferComments",
      "currentOfferGroups"
    ]),
    ...referenceState(copyState(["references", "isFetchingReferences"])),
    referencesRequestParams() {
      return {
        filter: {
          offer_id: this.currentOfferId
        }
      };
    },
    formattedReferences() {
      return getReferences(this.references);
    },
    application() {
      return this.currentOffer.application;
    },
    role() {
      return this.application.booking && this.application.booking.title;
    },
    candidateName() {
      return this.application ? getCandidateName(this.application) : "";
    },
    candidate() {
      return this.application.user;
    },
    currentOfferId() {
      return this.$route.params.id;
    },
    commentsSize() {
      return size(this.offerComments);
    },
    shouldFetchComments() {
      return isPermissioned(`offers.view.comments`);
    },
    showReferences() {
      return size(this.formattedReferences);
    },
    bookingId() {
      return this.application.booking && this.application.booking.id;
    },
    pageTitle() {
      return `${this.candidateName} - ${this.role} - Offers - Track`;
    },
    bookingLink() {
      return this.bookingId ? `R-${this.bookingId} ${this.role}` : "N/A";
    },
    contracts() {
      return this.currentOffer.contracts;
    }
  },
  methods: {
    ...mapOfferActions({
      fetchCurrentOffer: FETCH_CURRENT_OFFER,
      fetchOfferComments: FETCH_OFFER_COMMENTS,
      addComment: ADD_OFFER_COMMENT
    }),
    ...referenceActions({
      fetchReferences: FETCH_REFERENCES
    }),
    async updateReferences() {
      await this.fetchReferences(this.referencesRequestParams);
    },
    openCommentsSection() {
      this.showComments = true;
      scrollToPageTop();
      if (this.$refs.comments) {
        this.$refs.comments.focusOnTextField();
      }
    },
    async createNewComment(comment) {
      this.isAddingComments = true;
      await this.addComment({
        body: comment,
        commentableType: "offer",
        commentableId: this.currentOfferId
      });
      this.isAddingComments = false;
    },
    viewBooking() {
      this.$router.push({
        name: "bookingDetails",
        params: { id: this.bookingId }
      });
    },
    adjustOfferGroups(offerGroups) {
      this.sortedOfferGroups = [...getSortedOfferGroups(offerGroups)];
    },
    getColorByStatus
  },
  watch: {
    currentOfferGroups(newValue) {
      this.adjustOfferGroups(newValue);
    }
  }
};
</script>

<style lang="scss" scoped>
.offers {
  @media only screen and (max-width: $mobile-breakpoint - 1) {
    margin-bottom: 150px;
  }
}
</style>
